import React, { Fragment, useEffect, useRef, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

interface MultiSelectDropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface MultiSelectDropdownProps {
  options: MultiSelectDropdownOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  maxDisplayItems?: number;
  disabled?: boolean;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select items...",
  label,
  className = "",
  maxDisplayItems = 2,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle select/deselect all
  const handleSelectAll = () => {
    if (disabled) return;
    if (value.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((opt) => opt.value));
    }
  };

  // Handle individual selection
  const handleToggleOption = (optionValue: string) => {
    if (disabled) return;
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  // Format display text
  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder;
    }
    if (value.length === options.length) {
      return "All Selected";
    }
    if (value.length > maxDisplayItems) {
      return `${value.length} Selected`;
    }
    return options
      .filter((opt) => value.includes(opt.value))
      .map((opt) => opt.label)
      .join(", ");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1">
          <span className="inline-flex items-baseline gap-1.5">
            {label}
            {value.length > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 leading-none">
                {value.length}
              </span>
            )}
          </span>
        </label>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={classNames(
            "relative w-full rounded-md py-2 pl-3 pr-10 text-left border shadow-sm sm:text-sm",
            disabled
              ? "bg-gray-50 border-gray-200 cursor-not-allowed text-gray-500"
              : "bg-white border-gray-300 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
          )}
          aria-disabled={disabled}
        >
          <span
            className={classNames(
              "block truncate",
              disabled ? "text-gray-400" : "",
            )}
          >
            {disabled && options.length === 0
              ? "No options available"
              : getDisplayText()}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className={classNames(
                "h-5 w-5",
                disabled ? "text-gray-300" : "text-gray-400",
              )}
              aria-hidden="true"
            />
          </span>
        </button>

        <Transition
          show={isOpen}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg">
            <ul className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-black/5 overflow-auto focus:outline-none sm:text-sm">
              {/* Select All / Clear All */}
              <li>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm font-medium text-gray-900 border-b border-gray-200"
                >
                  {value.length === options.length ? "Clear All" : "Select All"}
                </button>
              </li>

              {/* Options */}
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <li
                    key={option.value}
                    onClick={() => handleToggleOption(option.value)}
                    className="cursor-pointer select-none relative py-2 pl-10 pr-4 hover:bg-gray-100"
                  >
                    <span
                      className={classNames(
                        isSelected ? "font-medium" : "font-normal",
                        "block truncate",
                      )}
                    >
                      <div className="flex items-center">
                        {option.icon && (
                          <span className="mr-2 flex-shrink-0">
                            {option.icon}
                          </span>
                        )}
                        {option.label}
                      </div>
                    </span>
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </Transition>
      </div>
    </div>
  );
};
